import { Bleed, Button, Checkbox, colors, Separator, useStatusOverlay } from '@fs/zion-ui'
import React, { useState, useCallback } from 'react'
import { Trans } from 'react-i18next'
import { css } from '@emotion/core'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import axios from 'axios'
import { sgBaseUrl, baseUrl } from '@fs/zion-config'
import { useUser } from '@fs/zion-user'
import transl from './MetadataAuditInfoSheet.transl'
import fsOperationsCallHeader from '../../utils/fsOperationsCallHeader'
import query from './queries'
import { useGroupData } from '../imageBrowse/hooks'
import { getCurrentWorkflowActions, workflowStatuses } from '../imageBrowse/helpers'

function isValidId(id) {
  const idNumber = Number(id)
  return !Number.isNaN(idNumber) && idNumber > 0
}

function hasValidCoverages(coverages = []) {
  for (let i = 0; i < coverages.length; i++) {
    const coverage = coverages[i]
    if (!isValidId(coverage?.place?.id)) return false

    const recordTypeWithName = coverage.recordTypes.find(({ id }) => isValidId(id))
    if (!recordTypeWithName) return false

    const recordTypeWithDate = coverage.recordTypes.find(({ date }) => Array.isArray(date) && date.length > 0)
    if (!recordTypeWithDate) return false
  }

  return true
}

const infoSheetFooter = css`
  position: fixed;
  bottom: 0;
  background: ${colors.gray00};
  width: 100%;
  box-shadow: 0 -2px 10px #ddd;
  padding: 10px 16px;
`

const MetadataAuditInfoSheetFooter = ({ saveCoverages = null }) => {
  const [preSaveCheck, setPreSaveCheck] = useState()
  const { group } = useGroupData()
  const showStatusOverlay = useStatusOverlay()
  const { properties = [], id = null } = group
  const { cisId } = useUser()

  const [fetchWorkflowActions] = useLazyQuery(query.workflowActions(), {
    fetchPolicy: 'no-cache',
  })
  const [doUpdateWorkflowActionStatus] = useMutation(query.updateWorkflowActionStatus())

  const submitMetadataAudit = useCallback(async () => {
    if (saveCoverages) await saveCoverages()

    const currentWorkflowActions = await getCurrentWorkflowActions(fetchWorkflowActions, group)
    // call Metis with ImageMetadataAudit set to complete
    const foundImageMetadataAuditWorkflowAction = currentWorkflowActions?.find(
      (action) => action?.name === workflowStatuses.IMAGE_METADATA_AUDIT && action?.status !== 'Completed'
    )

    if (!hasValidCoverages(group?.coverages)) {
      showStatusOverlay({
        message: transl.invalidCoverage(),
        type: 'error',
        dismissable: true,
      })
      return
    }

    if (foundImageMetadataAuditWorkflowAction) {
      try {
        const metisProjectId = properties.find((property) => property.name === 'metisProjectId').values[0]
        const startMetadataAuditUrl = `${sgBaseUrl}/service/records/metis/cache/audit/tasks/project/${metisProjectId}/group/${id}/action/ImageMetadataAudit/start`
        await axios.put(startMetadataAuditUrl)
      } catch (e) {
        console.error(`Error removing the audit from cache: ${e}`)
      }

      const metisAuditResponse = await doUpdateWorkflowActionStatus({
        variables: {
          params: {
            action: workflowStatuses.IMAGE_METADATA_AUDIT,
            newStatus: 'Completed',
            rmsId: group?.id,
            createNewWorkflowAction: false,
          },
        },
        context: { headers: { ...fsOperationsCallHeader } },
      })
      if (!metisAuditResponse?.data?.updateMetisProjectStatus?.code === '204') {
        console.error(`metisAuditResponse returned code of ${metisAuditResponse?.data?.updateMetisProjectStatus?.code}`)
      }
    }

    let redirectQuery = ''
    try {
      const storedData = JSON.parse(localStorage.getItem('react-audit-data'))
      if (storedData) redirectQuery = `/${storedData.projectId}?auditCompletedFor=${group.id}`
    } catch {
      redirectQuery = ''
    }

    const groupHasMissingCoverageData = currentWorkflowActions.find(
      (wfa) => wfa.name === 'AuditMissingCoverage' && wfa.status === 'Available'
    )

    if (groupHasMissingCoverageData) {
      const coverageDataWFAResponse = await doUpdateWorkflowActionStatus({
        variables: {
          params: {
            action: 'AuditMissingCoverage',
            newStatus: 'Completed',
            rmsId: group?.id,
            createNewWorkflowAction: false,
            comment: `reviewed by user ${cisId}`,
          },
        },
        context: { headers: { ...fsOperationsCallHeader } },
      })
      if (!coverageDataWFAResponse?.data?.updateMetisProjectStatus?.code === '204') {
        console.error(
          `metisAuditResponse returned code of ${coverageDataWFAResponse?.data?.updateMetisProjectStatus?.code}`
        )
      }
    }

    const redirectBase = process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : baseUrl
    window.location.assign(`${redirectBase}/records/audit${redirectQuery}`)
  }, [
    saveCoverages,
    fetchWorkflowActions,
    group,
    showStatusOverlay,
    id,
    properties,
    doUpdateWorkflowActionStatus,
    cisId,
  ])

  return (
    <Bleed sides>
      <div css={infoSheetFooter}>
        <Checkbox
          label={
            <Trans
              i18nKey="groupData.verifiedAllMetadataCaption"
              defaults="I have verified <strong>all</strong> of the metadata fields for this folder."
            />
          }
          name="PreSaveCheckbox"
          checked={preSaveCheck}
          onChange={({ target }) => setPreSaveCheck(target.checked)}
        />
        <Separator size="xs" />
        <Button emphasis="high" fullWidth centered disabled={!preSaveCheck} onClick={submitMetadataAudit}>
          {transl.submitAudit()}
        </Button>
      </div>
    </Bleed>
  )
}

export default MetadataAuditInfoSheetFooter
